import { Button, buttonVariants } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Link, useParams } from 'react-router-dom';
import { ArrowUpLeftFromSquare } from 'lucide-react';
import { FieldValues, useForm } from 'react-hook-form';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageTitle } from '@/components/PageTitle';
import { toast } from '@/hooks/use-toast';
import {
  useReadExternalConnection,
  useReindexExternalConnection,
  useUpdateExternalConnection,
} from '@/hooks/externalConnectionsApi';
import { useAuthStore } from '@/store/authStore';
import {
  connectionTypeName,
  ExternalConnectionType,
} from '@/lib/integrationTypes/externalConnectionTypes';
import { queryClient } from '@/lib/queryClient';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Textarea } from '@/components/ui/textarea';
import ConnectionForm from '../AddConnection/ConnectionForm';
import ConnectionDataPreview from '../AddConnection/ConnectionDataPreview';
import { AxiosError } from 'axios';
import { Spinner } from '@/components/ui/spinner';
import { formatDistanceToNow } from 'date-fns';
import ConnectionSidebar from './ConnectionSidebar';
import DeviceCredentials from '@/features/Devices/components/DeviceCredentials';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import CamSneakPreview from '@/app/Sites/components/CamSneakPreview';
import { AspectRatio } from '@radix-ui/react-aspect-ratio';

const detailsFormSchema = z.object({
  name: z.string().min(2, {
    message: 'Connection Name must be at least 2 characters.',
  }),
  description: z.string().optional(),
});

const EditConnection = () => {
  const { connectionId } = useParams();

  const {
    data: extConnRes,
    isLoading: isExternalConnectionLoading,
    error: externalConnectionError,
    isError: isExtConnectionError,
  } = useReadExternalConnection({ id: connectionId ?? '' });

  const { extConnectionData, devices } = extConnRes
    ? {
      extConnectionData: extConnRes.externalConnection,
      devices: extConnRes.devices,
    }
    : {};

  const authStore = useAuthStore();

  const {
    data: reIndexConnectionResult,
    error: reindexError,
    isPending: isReIndexPending,
    mutateAsync: reIndexConnection,
    submittedAt: reIndexSubmittedAt,
  } = useReindexExternalConnection({ id: connectionId ?? '', queryClient });

  const {
    mutateAsync: saveExtConnection,
    isPending: savingExtConnection,
    error: saveError,
    submittedAt: saveSubmittedAt,
  } = useUpdateExternalConnection({ id: connectionId ?? '', queryClient });

  let { connectionTypeDisplay, connectionType } = useMemo(() => {
    if (!extConnectionData) {
      return {
        connectionTypeDisplay: '',
        connectionType: ExternalConnectionType.ExternalService,
      };
    }

    return {
      connectionTypeDisplay: connectionTypeName[extConnectionData.type],
      connectionType: extConnectionData.type,
    };
  }, [extConnectionData]);

  const [form, setForm] = useState<FieldValues | null>(null);

  const detailsForm = useForm<z.infer<typeof detailsFormSchema>>({
    resolver: zodResolver(detailsFormSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  });

  useEffect(() => {
    if (extConnectionData) {
      detailsForm.reset({
        name: extConnectionData.name,
        description: extConnectionData.description,
      });
    }
  }, [extConnectionData, detailsForm]);

  const currentState = useCallback(() => {
    if (!extConnectionData) {
      return undefined;
    }
    return {
      ...extConnectionData,
      integrationFields: form || extConnectionData.integrationFields,
      name: detailsForm?.getValues('name'),
      description: detailsForm?.getValues('description') || '',
    };
  }, [extConnectionData, form, detailsForm]);

  const savePage = useCallback(async () => {
    let state = currentState();
    console.log('Full submit...', form, state);
    if (!state) {
      console.error('No form data');
      toast({
        title: 'Failed to save',
        description: 'Ensure all fields are filled',
        variant: 'destructive',
      });
      return;
    }

    try {
      console.log('Sending state', state);
      let result = await saveExtConnection(state);
      console.log('Result:', result);

      toast({
        title: 'Connection saved',
      });
    } catch (e) {
      toast({
        title: 'Failed to create connection',
        description: `${e}`,
        variant: 'destructive',
      });
    }
  }, [form, currentState, saveExtConnection]);

  async function submitReIndex(values: FieldValues) {
    const state = currentState();
    if (!state) {
      console.error('No connection loaded');
      return;
    }

    console.log('Submitted values:', values);
    await reIndexConnection(state);
  }

  if (isExternalConnectionLoading) {
    return (
      <div className="flex h-[calc(100vh-3rem)] items-center justify-center">
        <h1 className="font-sans text-2xl font-bold text-white">
          <Spinner size={'large'} className="text-white" />
        </h1>
      </div>
    );
  }

  if (isExtConnectionError || !extConnectionData) {
    if (externalConnectionError instanceof AxiosError && externalConnectionError.status === 404) {
      return (
        <div className="flex h-[calc(100vh-3rem)] flex-col items-center justify-center gap-6">
          <h1 className="font-sans text-2xl font-bold text-white">External Connection not found</h1>
          <h1 className="font-sans text-xl text-white">
            Check you have signed into the right account.
          </h1>
          <Link className={buttonVariants()} to="/connections">
            Go Back
          </Link>
        </div>
      );
    }

    return (
      <div className="flex h-[calc(100vh-3rem)] flex-col items-center justify-center gap-6">
        <h1 className="font-sans text-2xl font-bold text-white">Could not fetch device</h1>
        <Link className={buttonVariants()} to="/connections">
          Go Back
        </Link>
      </div>
    );
  }

  return (
    <div>
      <PageTitle title="Edit Connection" />
      <div className="sticky top-14 z-10 flex items-center justify-between border-b bg-[#111] text-white" data-onboarding="connection-detail-header">
        <div className="flex w-max items-center">
          <Link to="/connections" className="flex-shrink-0 border-r p-4">
            <img src="/icon/chevron-left.svg" className="h-6 w-6" />
          </Link>
          <h1 className="mx-4 justify-center font-semibold">External Connection</h1>

          <p className="ml-4 text-sm text-gray-500" title="Current Tenant">
            {authStore?.getTenantData()?.tenantName ||
              authStore?.getTenantData()?.tenantId ||
              'Unknown Tenant'}
          </p>
        </div>
        <div className="mr-2 flex items-center gap-4 text-white">
          <p className="text- text-xs text-gray-400">
            Last updated{' '}
            {formatDistanceToNow(
              new Date(saveSubmittedAt || extConnectionData?.updated || new Date()),
              { addSuffix: true }
            )}
          </p>
          <Button className="min-w-48" disabled={savingExtConnection} onClick={savePage} data-onboarding="connection-detail-save-button">
            Save
          </Button>
        </div>
      </div>
      <section className="mx-auto grid w-full grid-cols-[1fr_360px] text-white">
        <Tabs defaultValue="details">
          <TabsList className="flex" data-onboarding="connection-detail-tabs">
            <TabsTrigger value="details">External Connection Details</TabsTrigger>
            {/* <TabsTrigger value="devices">Connected Devices</TabsTrigger> */}
          </TabsList>
          <TabsContent value="details" data-onboarding="connection-detail-tabs-content">
            <div className="mx-auto mt-8 w-full max-w-[1200px] px-6">
              <div className="w-full rounded-xl border bg-[#111] p-4" data-onboarding="connection-detail-info-card">
                <div className="flex items-center justify-between gap-4">
                  <h1 className="text-xl font-semibold">{extConnectionData.name}</h1>
                  {authStore.roles?.includes('SuperAdmin') ? (
                    <Link
                      to={`/ihub/integration/${extConnectionData.integrationId}`}
                      target="_none"
                      className={buttonVariants({ variant: 'outline' })}
                    >
                      <ArrowUpLeftFromSquare /> {extConnectionData.integration.name}
                    </Link>
                  ) : (
                    <></>
                  )}
                </div>
                <p className="text-sm text-gray-500">{extConnectionData.description}</p>
              </div>
              <div className="my-6">
                <section className="mt-6 rounded-xl border bg-[#111]" data-onboarding="connection-detail-form">
                  <h2 className="border-b p-6 text-xl font-semibold">About this Device</h2>
                  <div className="p-6">
                    <Form {...detailsForm}>
                      <div className="mt-4 flex flex-wrap gap-4">
                        <FormField
                          control={detailsForm.control}
                          name="name"
                          render={({ field }) => (
                            <FormItem className="grid flex-1 items-center gap-1.5">
                              <FormLabel>Connection Name</FormLabel>
                              <FormControl>
                                <Input placeholder="Enter device name" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      <div className="mt-4 flex flex-wrap gap-4"></div>

                      <div className="mt-4">
                        <div className="grid w-full gap-1.5">
                          <FormField
                            control={detailsForm.control}
                            name="description"
                            render={({ field }) => (
                              <FormItem className="grid flex-1 items-center gap-1.5">
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                  <Textarea
                                    placeholder="Enter device description or notes"
                                    className="min-h-[120px]"
                                    {...field}
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                      </div>
                    </Form>
                  </div>
                </section>

                <div data-onboarding="connection-detail-credentials">
                  <DeviceCredentials device={extConnectionData} />
                </div>

                <div data-onboarding="connection-detail-connection-form">
                  <ConnectionForm
                    integration={extConnectionData.integration}
                    onVariableSubmit={submitReIndex}
                    onFormChange={setForm}
                    currentlyProcessing={false}
                    submitText="Reindex Connection"
                    logs={reIndexConnectionResult?.scriptLog}
                    defaultValues={extConnectionData.integrationFields}
                  />
                </div>
                <ConnectionDataPreview
                  loading={isExternalConnectionLoading || isReIndexPending}
                  connectionData={extConnectionData}
                  integration={extConnectionData.integration}
                  dataDate={new Date(extConnectionData.lastScanTime)}
                />
              </div>
            </div>
          </TabsContent>
          {/* <TabsContent
            value="devices"
            className="grid w-full grid-cols-1 gap-4 p-4 pt-0 md:grid-cols-2 lg:grid-cols-3"
          >
            {devices?.map((device) => (
              <Link to={`/devices/${device.device.id}/edit`} className="block">
                <div className="relative rounded-lg border border-gray-700 bg-[#111] transition-colors hover:border-lime">
                  <AspectRatio ratio={16 / 9}>
                    <div className="flex h-full items-start justify-between border-b p-4">
                      <div className="z-10">
                        <h2 className="text-xl font-semibold text-lime">
                          {device.device.description}
                        </h2>
                        <p className="text-gray-400">{device.device.externalId}</p>
                      </div>
                    </div>
                    <div>
                      <CamSneakPreview VPCs={device.cameras.map((x) => x.id)} />
                    </div>
                  </AspectRatio>
                </div>
              </Link>
            ))}
          </TabsContent> */}
        </Tabs>
        <div data-onboarding="connection-detail-sidebar">
          <ConnectionSidebar externalConnection={extConnectionData} />
        </div>
      </section>
    </div>
  );
};

export default EditConnection;
